import { NODE_HEIGHT, NODE_WIDTH } from "./treeGraph.constants";

/** Silhouette outline as 0–1 fractions of the shape's own bounding box. */
type ShapePoints = ReadonlyArray<readonly [number, number]>;

export type MiniMapShape = {
	/** The canvas shape's own width ÷ height: what keeps a circle a circle inside the taller node box. */
	aspect: number;
	fill: string;
	stroke: string;
	/** SVG path for the silhouette drawn with its bounding box at (x, y), sized width × height. */
	toPath: (x: number, y: number, width: number, height: number) => string;
	/**
	 * Where the initials sit, as a fraction of the shape's height from its top. Defaults to the middle;
	 * only a shape whose body is not centred in its box (the place pin's round head) sets it.
	 */
	labelCenterY?: number;
};

export type ShapeBox = {
	height: number;
	width: number;
	x: number;
	y: number;
};

/** A hundredth of a flow unit is finer than the minimap can ever draw, and keeps float noise out of the path. */
const round2 = (value: number): number => Math.round(value * 100) / 100;

const polygonPath =
	(points: ShapePoints) =>
	(x: number, y: number, width: number, height: number): string =>
		`${points
			.map(
				([pointX, pointY]: readonly [number, number], index: number) =>
					`${index === 0 ? "M" : "L"}${round2(x + pointX * width)} ${round2(y + pointY * height)}`
			)
			.join(" ")} Z`;

const circlePath = (x: number, y: number, width: number, height: number): string => {
	const radius = round2(width / 2);
	const left = round2(x);
	const right = round2(x + width);
	const centerY = round2(y + height / 2);

	return `M${left} ${centerY}A${radius} ${radius} 0 1 0 ${right} ${centerY}A${radius} ${radius} 0 1 0 ${left} ${centerY} Z`;
};

/** Corner radius as a fraction of the card's width, from .graphCard's 12px on a 132px node. */
const CARD_RADIUS_RATIO = 12 / NODE_WIDTH;

const cardPath = (x: number, y: number, width: number, height: number): string => {
	const radius = round2(width * CARD_RADIUS_RATIO);
	const arc = `A${radius} ${radius} 0 0 1`;

	return [
		`M${round2(x + radius)} ${round2(y)}`,
		`H${round2(x + width - radius)}`,
		`${arc} ${round2(x + width)} ${round2(y + radius)}`,
		`V${round2(y + height - radius)}`,
		`${arc} ${round2(x + width - radius)} ${round2(y + height)}`,
		`H${round2(x + radius)}`,
		`${arc} ${round2(x)} ${round2(y + height - radius)}`,
		`V${round2(y + radius)}`,
		`${arc} ${round2(x + radius)} ${round2(y)}`,
		"Z"
	].join(" ");
};

/*
 * .graphShapePin is a square with three 50% corners rotated -45°, which paints a circle with the one
 * square corner poking out below it. Centre-to-corner is radius × √2, so the tip sits that far under
 * the centre and the two tangent lines meet the circle at 45° either side of straight down.
 */
const pinPath = (x: number, y: number, width: number, height: number): string => {
	const radius = width / 2;
	const centerX = x + radius;
	const centerY = y + radius;
	const tangent = radius * Math.SQRT1_2;
	const start = `${round2(centerX - tangent)} ${round2(centerY + tangent)}`;
	const end = `${round2(centerX + tangent)} ${round2(centerY + tangent)}`;

	return `M${start}A${round2(radius)} ${round2(radius)} 0 1 1 ${end}L${round2(centerX)} ${round2(y + height)} Z`;
};

/** Circle radius plus the tip's radius × √2 overhang, over the width of 2 radii. */
const PIN_ASPECT = 2 / (1 + Math.SQRT2);

/*
 * One entry per canvas silhouette. Polygon points are the clip-path percentages from
 * GraphNodeItem.module.css as 0–1 fractions, and `aspect` is that same rule's width ÷ height.
 *
 * Fill is each canvas gradient's light stop and stroke its dark stop: a gradient collapses to a flat
 * tone at minimap scale, and the outline is what keeps neighbouring nodes apart. Person is the one
 * approximation — its canvas card is cream around a gold image tile, but cream would disappear
 * against the minimap, so the card takes the tile's gold.
 */
const MINIMAP_SHAPES: Record<string, MiniMapShape> = {
	Person: {
		aspect: NODE_WIDTH / NODE_HEIGHT,
		fill: "#8e6f25",
		stroke: "#453612",
		toPath: cardPath
	},
	Object: {
		aspect: 1,
		fill: "#8e6f25",
		stroke: "#453612",
		toPath: circlePath
	},
	Place: {
		aspect: PIN_ASPECT,
		fill: "#617c4e",
		stroke: "#35442b",
		toPath: pinPath,
		// The head's centre is one radius (half the width) down; as a fraction of the height that is aspect ÷ 2.
		labelCenterY: PIN_ASPECT / 2
	},
	Creature: {
		aspect: 80 / 90,
		fill: "#8e6f34",
		stroke: "#4b3a1b",
		toPath: polygonPath([
			[0.5, 0],
			[1, 0.25],
			[1, 0.75],
			[0.5, 1],
			[0, 0.75],
			[0, 0.25]
		])
	},
	Organization: {
		aspect: 82 / 88,
		fill: "#597c60",
		stroke: "#2a5240",
		toPath: polygonPath([
			[0, 0.04],
			[0.5, 0],
			[1, 0.04],
			[1, 0.6],
			[0.5, 1],
			[0, 0.6]
		])
	},
	Event: {
		aspect: 1,
		fill: "#9a6b2a",
		stroke: "#523916",
		toPath: polygonPath([
			[0.5, 0],
			[1, 0.5],
			[0.5, 1],
			[0, 0.5]
		])
	},
	Species: {
		aspect: 1,
		fill: "#657b48",
		stroke: "#354126",
		toPath: polygonPath([
			[0.3, 0],
			[0.7, 0],
			[1, 0.3],
			[1, 0.7],
			[0.7, 1],
			[0.3, 1],
			[0, 0.7],
			[0, 0.3]
		])
	},
	Concept: {
		aspect: 1,
		fill: "#847151",
		stroke: "#4b402e",
		toPath: polygonPath([
			[0.5, 0],
			[0.62, 0.33],
			[0.98, 0.35],
			[0.7, 0.57],
			[0.79, 0.92],
			[0.5, 0.72],
			[0.21, 0.92],
			[0.3, 0.57],
			[0.02, 0.35],
			[0.38, 0.33]
		])
	},
	Custom: {
		aspect: 88 / 80,
		fill: "#817256",
		stroke: "#4a4131",
		toPath: polygonPath([
			[0.19, 0],
			[0.81, 0],
			[1, 1],
			[0, 1]
		])
	}
};

/** The evergreen glyph LinkedTreeNode draws; its trunk is too thin to read, so the whole tree takes the foliage green. */
export const LINKED_TREE_SHAPE: MiniMapShape = {
	aspect: 86 / 96,
	fill: "#4f7d55",
	stroke: "#24503b",
	toPath: polygonPath([
		[0.5, 0],
		[0.78, 0.26],
		[0.66, 0.26],
		[0.9, 0.55],
		[0.76, 0.55],
		[0.98, 0.84],
		[0.57, 0.84],
		[0.57, 1],
		[0.43, 1],
		[0.43, 0.84],
		[0.02, 0.84],
		[0.24, 0.55],
		[0.1, 0.55],
		[0.34, 0.26],
		[0.22, 0.26]
	])
};

/** Unknown types fall back to the trapezoid, the same silhouette getShapeClass gives them on canvas. */
export const getMiniMapShape = (type: string | undefined): MiniMapShape =>
	(type && MINIMAP_SHAPES[type]) || MINIMAP_SHAPES.Custom;

/**
 * Sizes a silhouette inside a minimap node's box: as wide as it fits without stretching, centred
 * across the box and pinned to its top, which is where the shape sits on canvas with the title
 * label taking the height below it.
 */
export const fitShapeBox = (shape: MiniMapShape, x: number, y: number, width: number, height: number): ShapeBox => {
	const boxWidth = Math.min(width, height * shape.aspect);

	return {
		height: boxWidth / shape.aspect,
		width: boxWidth,
		x: x + (width - boxWidth) / 2,
		y
	};
};
